import { useQuery } from "@tanstack/react-query"
import { doc, Firestore, getDoc } from "firebase/firestore"
import { useEffect, useMemo, useState } from "react"

import { MaintenanceType } from "../types"

export const INTERVAL_FOR_MAINTENANCE_API = 2 * 60 * 1000 // 2 minutes

type Props = {
  firebaseDb: Firestore
  platform: string
}

type MaintenanceData = {
  isMaintenanceActive: boolean
  startTime: number
  endTime: number
  platforms?: string[]
}

export function useMaintenance({ firebaseDb, platform }: Props) {
  const { data: rawData, refetch } = useQuery({
    queryKey: ["maintenance", platform],
    queryFn: () => getMaintenanceData(firebaseDb),
    refetchInterval: INTERVAL_FOR_MAINTENANCE_API,
    refetchOnWindowFocus: "always",
  })

  const details = useMemo<MaintenanceData>(() => {
    if (!rawData) {
      return {
        /**
         * This is by default `false`
         * because we don't want to show maintenance page glimpse on every reload
         */
        isMaintenanceActive: false,
        startTime: 0,
        endTime: 0,
        platforms: undefined,
      }
    }

    const maintenanceData: MaintenanceData = {
      isMaintenanceActive: false,
      startTime: rawData.startsAt.seconds * 1000,
      endTime: rawData.endsAt.seconds * 1000,
      platforms: rawData.platforms,
    }

    const secondsLeft = Math.max(
      0,
      (maintenanceData.endTime - Date.now()) / 1000
    )

    // Show maintenance logs only if time left is more than 0 seconds
    if (secondsLeft > 0) {
      console.group("Maintenance Mode")
      console.log(
        "Start Time:",
        new Date(maintenanceData.startTime).toLocaleString()
      )
      console.log(
        "End Time:",
        new Date(maintenanceData.endTime).toLocaleString()
      )
      console.log("Platforms:", maintenanceData.platforms?.join(", ") || "All")
      console.log(
        "Time left (seconds):",
        Math.max(0, (maintenanceData.endTime - Date.now()) / 1000)
      )
      console.groupEnd()
    }

    const currentTime = new Date().getTime()
    /**
     * if currentTime is between startTime and endTime then set maintenance as true
     */
    if (
      currentTime >= maintenanceData.startTime &&
      currentTime <= maintenanceData.endTime
    ) {
      // All platforms are down
      if (rawData.platforms === undefined || rawData.platforms.length === 0) {
        maintenanceData.isMaintenanceActive = true
      }

      // Specific platform is down
      else if (rawData.platforms.includes(platform))
        maintenanceData.isMaintenanceActive = true
    } else {
      maintenanceData.isMaintenanceActive = false
    }

    return maintenanceData
  }, [rawData, platform])

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  useEffect(() => {
    const interval = setInterval(() => {
      const left = calculateTimeLeft(details.endTime)
      setTimeLeft(left)

      const hasEnded = left.distance <= 0
      if (hasEnded) {
        clearInterval(interval)
        refetch()
      }
    }, 1000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details.endTime])

  return { ...details, timeLeft }
}

const getMaintenanceData = async (firebaseDb: Firestore) => {
  console.log("Fetching maintenance data from firestore...")
  const docRef = doc(firebaseDb, "maintenance", "appInitConfig")
  const docSnap = await getDoc(docRef)

  if (docSnap.exists()) {
    const data = docSnap.data()
    return data as MaintenanceType
  }

  return undefined
}

const calculateTimeLeft = (endTime: number) => {
  const now = new Date().getTime()
  const distance = endTime - now

  const hours = Math.floor(
    (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  )
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((distance % (1000 * 60)) / 1000)

  return { distance, hours, minutes, seconds }
}
